import * as React from "react";
import { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import { FormikHelpers } from "formik";

import { AffinityForm, FormView } from "affinityForm";
import { Layout } from "affinityUI/structures/layout";
import { SettingsConfirmation, SettingsForm } from "components/views/settings";
import { fbcValidationSchema, wbcValidationSchema } from "components/views/settings/settingsForm/validationSchema";
import { SettingsFormContext } from "components/views/settings/settingsFormContext";
import { HospitalConfigurationType } from "components/views/settings/types";
import { settingsErrorFieldConverter } from "lib/extrahorizon/settings/converters";

import { useHospitalConfiguration } from "../context";
import { HospitalConfiguration } from "../context/types";
import { useUpdateExhSettingsDocument } from "../hooks/useUpdateExhSettingsDocument";

export const EditSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hospitalConfig, region, documentId, refetch } = useHospitalConfiguration();
  const { showBoundary: onError } = useErrorBoundary();
  const { updateSettings, loading: isSubmitting } = useUpdateExhSettingsDocument();

  const mutateHospitalConfig = useCallback(
    async (values: HospitalConfiguration, formikHelpers?: FormikHelpers<HospitalConfiguration>) => {
      const result = await updateSettings(region, documentId, values);
      if (!result) {
        return;
      }

      if (result.status === "validation_error") {
        Object.entries(result.errors).forEach(([key, message]) => {
          formikHelpers?.setFieldError(settingsErrorFieldConverter(key) ?? key, message);
        });
        navigate("..");
        return;
      }

      if (result.status === "missing_document") {
        onError(
          new Error(
            "No settings document exists for this hospital. The hospital must be provisioned with a settings document before it can be edited."
          )
        );
        return;
      }

      await refetch();
      navigate("/settings");
    },
    [updateSettings, region, documentId, refetch, navigate, onError]
  );

  const handleCancel = useCallback(() => {
    navigate(`/`);
  }, [navigate]);

  return (
    <AffinityForm<HospitalConfigurationType>
      context={SettingsFormContext}
      initialData={hospitalConfig}
      validationSchema={region === "USA" ? wbcValidationSchema : fbcValidationSchema}
      confirmHandler={mutateHospitalConfig}
    >
      <FormView
        form={
          <Layout title="Settings" loading={isSubmitting}>
            <SettingsForm onCancel={handleCancel} />
          </Layout>
        }
        confirm={
          <Layout
            title="Set and confirm"
            showBackBtn
            backLink=".."
            backLinkText="Back to settings form"
            loading={isSubmitting}
          >
            <SettingsConfirmation onCancel={handleCancel} />
          </Layout>
        }
      />
    </AffinityForm>
  );
};
